import type { BadgeTone } from '@/components/ui/Badge';
import type { Option } from '@/constants/institute';
import { STUDENT_STATUS_LABEL } from '@/constants/studentStatus';
import type { AttendanceStatus } from '@/types/attendance';
import type {
  GuardianKind,
  RollNumberMode,
  RollNumberOrder,
  StudentLoginState,
  StudentStatus,
} from '@/types/student';

export { STUDENT_STATUS_LABEL };

/** The longer form, for the summary tiles where there is room to be exact. */
export const STUDENT_STATUS_DESCRIPTION = {
  ACTIVE: 'Currently attending',
  INACTIVE: 'On the register, not attending',
  TRANSFERRED: 'Left for another institute',
  ALUMNI: 'Completed their studies',
  SUSPENDED: 'Barred from attending for now — can be reactivated',
} as const satisfies Record<StudentStatus, string>;

/**
 * What suspending does, in the words both confirmation dialogs use. One copy,
 * because the promise it makes is kept by the server — two wordings of it are
 * how the old dialog came to promise a restore that did not exist.
 */
export const SUSPEND_EXPLAINER =
  'Nothing is deleted. They come off the attendance register, exam lists and homework, ' +
  'but stay on the Students list marked Suspended, with their record and fees kept. ' +
  'You can reactivate them at any time.';

/** The order a registrar reads them: who is here, who is not, who has moved on. */
export const STUDENT_STATUS_ORDER = ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'TRANSFERRED', 'ALUMNI'] as const;

export const STUDENT_STATUS_TONE = {
  ACTIVE: 'success',
  INACTIVE: 'warning',
  TRANSFERRED: 'neutral',
  ALUMNI: 'info',
  SUSPENDED: 'critical',
} as const satisfies Record<StudentStatus, BadgeTone>;

/**
 * How each standing is painted on the enrolment ring and its legend.
 *
 * Two rules pull against each other here and both have to hold.
 *
 * **In the console's family.** The chrome is indigo, so the register's own
 * colour is blue — a green or a teal would read as a different product with the
 * sidebar in shot. Enrolled, the standing most children are in, takes it.
 *
 * **Still four distinguishable slices.** Four neighbouring blues would be one
 * smear on a chart, so the other three step away from it — amber for the one
 * worth acting on, slate for those who have left, violet for those who
 * finished. Nothing is carried by colour alone: every slice has a legend row
 * with its name, its count and its share, and every row keeps its `Badge`.
 *
 * `stroke` is a literal colour because an SVG stroke cannot read a Tailwind
 * class the compiler never generated, and a `var()` would not survive this
 * page's own re-tinting. The rest are utilities so they follow light and dark
 * on their own.
 *
 * Beside `STUDENT_STATUS_TONE` rather than folded into it: that one says which
 * `Badge` a row wears, this one says how a slice of a chart is drawn, and a
 * single map trying to be both is how the two silently disagree.
 */
export const STUDENT_STATUS_PAINT = {
  ACTIVE: {
    stroke: '#2563eb',
    dot: 'bg-blue-600',
    soft: 'bg-blue-50 dark:bg-blue-950/50 border-blue-200 dark:border-blue-800/60',
    text: 'text-blue-700 dark:text-blue-300',
    ring: 'ring-blue-400/70',
  },
  INACTIVE: {
    stroke: '#f59e0b',
    dot: 'bg-amber-500',
    soft: 'bg-amber-50 dark:bg-amber-950/50 border-amber-200 dark:border-amber-800/60',
    text: 'text-amber-700 dark:text-amber-300',
    ring: 'ring-amber-400/70',
  },
  TRANSFERRED: {
    stroke: '#64748b',
    dot: 'bg-slate-500',
    soft: 'bg-slate-50 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700',
    text: 'text-slate-700 dark:text-slate-300',
    ring: 'ring-slate-400/70',
  },
  ALUMNI: {
    stroke: '#8b5cf6',
    dot: 'bg-violet-500',
    soft: 'bg-violet-50 dark:bg-violet-950/50 border-violet-200 dark:border-violet-800/60',
    text: 'text-violet-700 dark:text-violet-300',
    ring: 'ring-violet-400/70',
  },
  SUSPENDED: {
    stroke: '#e11d48',
    dot: 'bg-rose-600',
    soft: 'bg-rose-50 dark:bg-rose-950/50 border-rose-200 dark:border-rose-800/60',
    text: 'text-rose-700 dark:text-rose-300',
    ring: 'ring-rose-400/70',
  },
} as const satisfies Record<
  StudentStatus,
  { stroke: string; dot: string; soft: string; text: string; ring: string }
>;

/**
 * The standings the edit form offers. Inactive is no longer one of them — but a
 * record already saved as Inactive keeps it in the list, or the picker would
 * show some other status while the save quietly kept the old one.
 */
export function studentStatusOptions(current?: StudentStatus): Option<StudentStatus>[] {
  return (Object.entries(STUDENT_STATUS_LABEL) as [StudentStatus, string][])
    .filter(([value]) => value !== 'INACTIVE' || current === 'INACTIVE')
    .map(([value, label]) => ({ value, label }));
}

/**
 * The register's status filter: only the two standings the office works with
 * day to day. Transferred and alumni records still appear under "All" — the
 * filter narrows the list, it never hides anybody from it.
 */
export const STUDENT_FILTER_STATUS_OPTIONS: Option<StudentStatus>[] = (
  ['ACTIVE', 'SUSPENDED'] as const
).map((value) => ({ value, label: STUDENT_STATUS_LABEL[value] }));

/** How a registrar asks for the register. */
export const STUDENT_SORT_OPTIONS: Option[] = [
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'rollNumber', label: 'Roll number' },
  { value: 'admissionNumber', label: 'Admission number' },
  { value: 'newest', label: 'Most recently admitted' },
];

/** The orders a section can be numbered in — the ones Indian registers use. */
export const ROLL_NUMBER_ORDER_OPTIONS: Option<RollNumberOrder>[] = [
  { value: 'FIRST_NAME', label: 'First name (A–Z)' },
  { value: 'SURNAME', label: 'Surname, then first name' },
  { value: 'GIRLS_FIRST', label: 'Girls first, then boys' },
  { value: 'ADMISSION_NUMBER', label: 'Admission number' },
];

/** Only the unnumbered is the default: renumbering puts printed registers out of step. */
export const ROLL_NUMBER_MODE_OPTIONS: Option<RollNumberMode>[] = [
  { value: 'MISSING', label: 'Only students without a roll number' },
  { value: 'ALL', label: 'Renumber the whole section from 1' },
];

/** Rows per page. The first is the default, so the table and the server agree. */
export const STUDENT_ROWS_PER_PAGE = [10, 25, 50] as const;
export const STUDENTS_PER_PAGE = STUDENT_ROWS_PER_PAGE[0];

/**
 * How the roll describes a student's access to the student panel.
 *
 * One wording, read by the roll and the record screen, so the two cannot
 * describe the same student differently (CLAUDE.md §3).
 */
export const STUDENT_LOGIN_LABEL = {
  NONE: 'No login',
  PENDING: 'Link not used',
  ACTIVE: 'Can sign in',
} as const satisfies Record<StudentLoginState, string>;

export const STUDENT_LOGIN_TONE = {
  /** Not a fault: most school children have no address of their own. */
  NONE: 'neutral',
  /** Worth acting on — they were invited and still cannot get in. */
  PENDING: 'warning',
  ACTIVE: 'success',
} as const satisfies Record<StudentLoginState, BadgeTone>;

/** Today's register mark, as the student list words it. */
export const TODAY_ATTENDANCE_LABEL = {
  PRESENT: 'Present today',
  ABSENT: 'Absent today',
  LATE: 'Late today',
  LEAVE: 'On leave today',
} as const satisfies Record<AttendanceStatus, string>;

export const TODAY_ATTENDANCE_TONE = {
  PRESENT: 'success',
  ABSENT: 'critical',
  LATE: 'warning',
  LEAVE: 'info',
} as const satisfies Record<AttendanceStatus, BadgeTone>;

/** Neither a register mark nor approved leave for today — not an absence. */
export const TODAY_NOT_MARKED_LABEL = 'Not marked today';

/** What the roll and its export call the primary contact. */
export const GUARDIAN_LABEL = {
  FATHER: 'Father',
  MOTHER: 'Mother',
  GUARDIAN: 'Guardian',
} as const satisfies Record<GuardianKind, string>;
